/* eslint camelcase: "off" */
/**
 * Verification policy admin client.
 *
 * Mounted at /api/verification/admin/ in django-monolith. Auth0 bearer
 * tokens are attached by the Auth0 interceptor (same as every other axios
 * call).
 *
 * All mutating calls require an `X-Change-Reason` header — the django-monolith
 * service writes a PolicyChangeLog row keyed on it.
 */
import axios, { AxiosRequestConfig } from 'axios';

import {
  ActionAttempt,
  ActionPolicy,
  AllowedActions,
  CurrentEmployeeCredential,
  CursorPage,
  EmployeeCredential,
  EmployerVerificationPolicy,
  PolicyAction,
  PolicyActionCategory,
  PolicyChangeLog,
  PolicyDecision,
  PolicyRequirement,
  PolicyRequirementCategory,
} from '../types/VerificationTypes';

const ADMIN_BASE = '/api/verification/admin';

// Registries (categories, actions, requirements, policies) currently return a
// plain array — no DRF pagination wrapper. Asking for a large page_size keeps
// DRF's default pagination from silently truncating the response. The
// downstream policy-row builder and registry tables assume complete data.
const REGISTRY_PAGE_SIZE = 1000;

function warnIfPossiblyTruncated(label: string, count: number) {
  if (
    process.env.NODE_ENV !== 'production' &&
    count >= REGISTRY_PAGE_SIZE
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `[verificationClient] ${label} returned ${count} rows — at or above page_size cap. ` +
        'Response may be truncated; switch to cursor pagination.'
    );
  }
}

function withReason(reason: string, config: AxiosRequestConfig = {}): AxiosRequestConfig {
  return {
    ...config,
    headers: { ...(config.headers || {}), 'X-Change-Reason': reason },
  };
}

// ---------------------------------------------------------------------------
// Action categories
// ---------------------------------------------------------------------------

async function listActionCategories(): Promise<PolicyActionCategory[]> {
  const res = await axios.get<PolicyActionCategory[]>(`${ADMIN_BASE}/action-categories/`, {
    params: { page_size: REGISTRY_PAGE_SIZE },
  });
  const data = Array.isArray(res.data) ? res.data : [];
  warnIfPossiblyTruncated('listActionCategories', data.length);
  return data;
}

async function getActionCategory(code: string): Promise<PolicyActionCategory> {
  const res = await axios.get<PolicyActionCategory>(`${ADMIN_BASE}/action-categories/${code}/`);
  return res.data;
}

async function createActionCategory(
  payload: Partial<PolicyActionCategory>,
  reason: string
): Promise<PolicyActionCategory> {
  const res = await axios.post<PolicyActionCategory>(
    `${ADMIN_BASE}/action-categories/`,
    payload,
    withReason(reason)
  );
  return res.data;
}

async function updateActionCategory(
  code: string,
  payload: Partial<PolicyActionCategory>,
  reason: string
): Promise<PolicyActionCategory> {
  const res = await axios.patch<PolicyActionCategory>(
    `${ADMIN_BASE}/action-categories/${code}/`,
    payload,
    withReason(reason)
  );
  return res.data;
}

async function deleteActionCategory(code: string, reason: string): Promise<void> {
  await axios.delete(`${ADMIN_BASE}/action-categories/${code}/`, withReason(reason));
}

// ---------------------------------------------------------------------------
// Requirement categories
// ---------------------------------------------------------------------------

async function listRequirementCategories(): Promise<PolicyRequirementCategory[]> {
  const res = await axios.get<PolicyRequirementCategory[]>(
    `${ADMIN_BASE}/requirement-categories/`,
    { params: { page_size: REGISTRY_PAGE_SIZE } }
  );
  const data = Array.isArray(res.data) ? res.data : [];
  warnIfPossiblyTruncated('listRequirementCategories', data.length);
  return data;
}

async function getRequirementCategory(code: string): Promise<PolicyRequirementCategory> {
  const res = await axios.get<PolicyRequirementCategory>(
    `${ADMIN_BASE}/requirement-categories/${code}/`
  );
  return res.data;
}

async function createRequirementCategory(
  payload: Partial<PolicyRequirementCategory>,
  reason: string
): Promise<PolicyRequirementCategory> {
  const res = await axios.post<PolicyRequirementCategory>(
    `${ADMIN_BASE}/requirement-categories/`,
    payload,
    withReason(reason)
  );
  return res.data;
}

async function updateRequirementCategory(
  code: string,
  payload: Partial<PolicyRequirementCategory>,
  reason: string
): Promise<PolicyRequirementCategory> {
  const res = await axios.patch<PolicyRequirementCategory>(
    `${ADMIN_BASE}/requirement-categories/${code}/`,
    payload,
    withReason(reason)
  );
  return res.data;
}

async function deleteRequirementCategory(code: string, reason: string): Promise<void> {
  await axios.delete(`${ADMIN_BASE}/requirement-categories/${code}/`, withReason(reason));
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

async function listActions(): Promise<PolicyAction[]> {
  const res = await axios.get<PolicyAction[]>(`${ADMIN_BASE}/actions/`, {
    params: { page_size: REGISTRY_PAGE_SIZE },
  });
  const data = Array.isArray(res.data) ? res.data : [];
  warnIfPossiblyTruncated('listActions', data.length);
  return data;
}

async function getAction(code: string): Promise<PolicyAction> {
  const res = await axios.get<PolicyAction>(`${ADMIN_BASE}/actions/${code}/`);
  return res.data;
}

async function createAction(
  payload: { code: string; category_code: string; name: string; description?: string;
             is_sensitive?: boolean; is_active?: boolean },
  reason: string
): Promise<PolicyAction> {
  const res = await axios.post<PolicyAction>(`${ADMIN_BASE}/actions/`, payload, withReason(reason));
  return res.data;
}

async function updateAction(
  code: string,
  payload: Partial<PolicyAction> & { category_code?: string },
  reason: string
): Promise<PolicyAction> {
  const res = await axios.patch<PolicyAction>(
    `${ADMIN_BASE}/actions/${code}/`,
    payload,
    withReason(reason)
  );
  return res.data;
}

async function deleteAction(code: string, reason: string): Promise<void> {
  await axios.delete(`${ADMIN_BASE}/actions/${code}/`, withReason(reason));
}

// ---------------------------------------------------------------------------
// Requirements
// ---------------------------------------------------------------------------

async function listRequirements(): Promise<PolicyRequirement[]> {
  const res = await axios.get<PolicyRequirement[]>(`${ADMIN_BASE}/requirements/`, {
    params: { page_size: REGISTRY_PAGE_SIZE },
  });
  const data = Array.isArray(res.data) ? res.data : [];
  warnIfPossiblyTruncated('listRequirements', data.length);
  return data;
}

async function getRequirement(code: string): Promise<PolicyRequirement> {
  const res = await axios.get<PolicyRequirement>(`${ADMIN_BASE}/requirements/${code}/`);
  return res.data;
}

async function createRequirement(
  payload: Partial<PolicyRequirement> & { category_code: string },
  reason: string
): Promise<PolicyRequirement> {
  const res = await axios.post<PolicyRequirement>(
    `${ADMIN_BASE}/requirements/`,
    payload,
    withReason(reason)
  );
  return res.data;
}

async function updateRequirement(
  code: string,
  payload: Partial<PolicyRequirement> & { category_code?: string },
  reason: string
): Promise<PolicyRequirement> {
  const res = await axios.patch<PolicyRequirement>(
    `${ADMIN_BASE}/requirements/${code}/`,
    payload,
    withReason(reason)
  );
  return res.data;
}

async function deleteRequirement(code: string, reason: string): Promise<void> {
  await axios.delete(`${ADMIN_BASE}/requirements/${code}/`, withReason(reason));
}

// ---------------------------------------------------------------------------
// Action policies (default + employer overrides)
// ---------------------------------------------------------------------------

interface ListPoliciesParams {
  action_code?: string;
  employer_id?: number | 'null';
  // `segment_id=null` narrows to default + blanket rows (the historical
  // shape). `segment_id=<id>` narrows to one segment's overrides. Omit
  // to get all rows — used by PolicyDetailPage to fetch blanket + its
  // segment children in a single round-trip.
  segment_id?: number | 'null';
}

async function listPolicies(params: ListPoliciesParams = {}): Promise<ActionPolicy[]> {
  const res = await axios.get<ActionPolicy[]>(`${ADMIN_BASE}/policies/`, {
    params: { ...params, page_size: REGISTRY_PAGE_SIZE },
  });
  const data = Array.isArray(res.data) ? res.data : [];
  warnIfPossiblyTruncated('listPolicies', data.length);
  return data;
}

async function getPolicy(id: number): Promise<ActionPolicy> {
  const res = await axios.get<ActionPolicy>(`${ADMIN_BASE}/policies/${id}/`);
  return res.data;
}

async function createPolicy(
  payload: Omit<ActionPolicy, 'id' | 'date_created' | 'created_by'>,
  reason: string
): Promise<ActionPolicy> {
  const res = await axios.post<ActionPolicy>(`${ADMIN_BASE}/policies/`, payload, withReason(reason));
  return res.data;
}

async function updatePolicy(
  id: number,
  payload: Partial<ActionPolicy>,
  reason: string
): Promise<ActionPolicy> {
  const res = await axios.patch<ActionPolicy>(
    `${ADMIN_BASE}/policies/${id}/`,
    payload,
    withReason(reason)
  );
  return res.data;
}

async function deletePolicy(id: number, reason: string): Promise<void> {
  await axios.delete(`${ADMIN_BASE}/policies/${id}/`, withReason(reason));
}

// ---------------------------------------------------------------------------
// Employer verification policies
// ---------------------------------------------------------------------------

async function listEmployerPolicies(): Promise<EmployerVerificationPolicy[]> {
  const res = await axios.get<EmployerVerificationPolicy[]>(`${ADMIN_BASE}/employer-policies/`);
  return res.data;
}

async function getEmployerPolicy(employerId: number): Promise<EmployerVerificationPolicy> {
  const res = await axios.get<EmployerVerificationPolicy>(
    `${ADMIN_BASE}/employer-policies/${employerId}/`
  );
  return res.data;
}

async function upsertEmployerPolicy(
  employerId: number,
  payload: Partial<EmployerVerificationPolicy>,
  reason: string
): Promise<EmployerVerificationPolicy> {
  try {
    return (
      await axios.patch<EmployerVerificationPolicy>(
        `${ADMIN_BASE}/employer-policies/${employerId}/`,
        payload,
        withReason(reason)
      )
    ).data;
  } catch (err: unknown) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      return (
        await axios.post<EmployerVerificationPolicy>(
          `${ADMIN_BASE}/employer-policies/`,
          { ...payload, employer_id: employerId },
          withReason(reason)
        )
      ).data;
    }
    throw err;
  }
}

async function deleteEmployerPolicy(employerId: number, reason: string): Promise<void> {
  await axios.delete(`${ADMIN_BASE}/employer-policies/${employerId}/`, withReason(reason));
}

// ---------------------------------------------------------------------------
// Employee credentials (read-only)
// ---------------------------------------------------------------------------

async function listEmployeeCredentials(
  employeeId: number,
  requirementCode?: string
): Promise<EmployeeCredential[]> {
  const res = await axios.get<EmployeeCredential[]>(
    `${ADMIN_BASE}/employees/${employeeId}/credentials/`,
    { params: requirementCode ? { requirement_code: requirementCode } : {} }
  );
  return res.data;
}

async function getCurrentEmployeeCredentials(
  employeeId: number
): Promise<CurrentEmployeeCredential[]> {
  const res = await axios.get<CurrentEmployeeCredential[]>(
    `${ADMIN_BASE}/employees/${employeeId}/current/`
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Audit lists (cursor paginated)
// ---------------------------------------------------------------------------

interface ListAttemptsParams {
  employee_id?: number;
  action_code?: string;
  decision?: string;
  failed_only?: boolean;
  since?: string;
  until?: string;
  cursor?: string;
}

async function listActionAttempts(params: ListAttemptsParams = {}): Promise<CursorPage<ActionAttempt>> {
  const res = await axios.get<CursorPage<ActionAttempt>>(`${ADMIN_BASE}/attempts/`, { params });
  return res.data;
}

interface ListChangeLogParams {
  entity_type?: string;
  entity_id?: string;
  actor?: string;
  since?: string;
  cursor?: string;
}

async function listPolicyChangeLog(
  params: ListChangeLogParams = {}
): Promise<CursorPage<PolicyChangeLog>> {
  const res = await axios.get<CursorPage<PolicyChangeLog>>(`${ADMIN_BASE}/change-log/`, { params });
  return res.data;
}


// Identity Verifications fraud-review queue — list ID-photo uploads with
// the fraud_signals + fraud_severity surfaced at the top level so the
// table can sort / filter without parsing JSON metadata.
export type FraudSeverity = 'ok' | 'low' | 'medium' | 'high' | 'critical' | 'unknown';

export interface FraudSignals {
  dob_plausibility: 'future' | 'implausible_old' | 'implausible_young' | 'plausible' | null;
  expiry_status: 'valid' | 'expiring_soon' | 'expired' | 'long_expired' | 'n/a' | null;
  extracted_id_looks_synthetic: boolean | null;
  surname_consistent: 'exact' | 'approximate' | 'mismatch' | null;
  id_number_match: 'match' | 'mismatch' | null;
}

// Reviewer verdict set during manual fraud-team review. Mirrors the
// django ReviewOutcome TextChoices on VerificationDocument. Null until
// a reviewer touches the doc. See verification/models/verification_document.py
// for the per-outcome semantics.
export type ReviewOutcome =
  | 'confirm_fraud'
  | 'cleared'
  | 'accepted_with_override'
  | 'recapture_required'
  | 'inconclusive'
  | 'escalate';

export interface IdentityVerificationListItem {
  id: string;
  employee_id: number;
  // Resolved at serialize time on the django side from the bulk
  // employee_map context — may be null when the document references an
  // employee that's been deleted.
  employee_name?: string | null;
  employer_name?: string | null;
  doc_type: string;
  verification_status: string;
  extraction_method: string | null;
  captured_at: string;
  bsuid_at_capture: string | null;
  msisdn_at_capture: string | null;
  flow_session_id: string | null;
  attempt_no: number | null;
  fraud_signals: FraudSignals | null;
  fraud_severity: FraudSeverity | null;
  document_kind: 'sa_id' | 'passport' | null;
  // Reviewer annotation + verdict — both null until a reviewer touches
  // the doc. `reviewer_note` is free-text; `review_outcome` is one of the
  // ReviewOutcome enum values above.
  reviewer_note?: string | null;
  review_outcome?: ReviewOutcome | null;
}

interface ListIdentityVerificationsParams {
  employee_id?: number;
  severity?: FraudSeverity;
  severity_at_least?: FraudSeverity;
  status?: string;
  document_kind?: 'sa_id' | 'passport';
  // Comma-separated list, e.g. "id_photo,selfie". Default on the server
  // is "id_photo" so the fraud queue keeps its scope. The per-employee
  // capture timeline on EmployeeDetail opts in to the full set.
  doc_types?: string;
  since?: string;
  until?: string;
  cursor?: string;
}

async function listIdentityVerifications(
  params: ListIdentityVerificationsParams = {}
): Promise<CursorPage<IdentityVerificationListItem>> {
  const res = await axios.get<CursorPage<IdentityVerificationListItem>>(
    `${ADMIN_BASE}/identity-verifications/`,
    { params }
  );
  return res.data;
}


// ---------------------------------------------------------------------------
// Access log — every read of a verification document (signed_url /
// download / metadata) writes a DocumentAccessLog row. Required for
// POPIA SAR responses. Per-document scope lives at
// /admin/documents/<id>/access-log/; the list endpoint below is global.
// ---------------------------------------------------------------------------

export type DocumentAccessKind = 'download' | 'signed_url' | 'metadata';

export interface DocumentAccessLogRow {
  id: string;
  document: string;
  access_kind: DocumentAccessKind;
  actor: string;
  access_reason: string;
  ticket_ref: string | null;
  source_ip: string | null;
  user_agent: string | null;
  signed_url_id: string | null;
  date_created: string;
}

interface ListAccessLogParams {
  actor?: string;
  document_id?: string;
  access_kind?: DocumentAccessKind;
  since?: string;
  until?: string;
  cursor?: string;
}

async function listAccessLog(
  params: ListAccessLogParams = {}
): Promise<CursorPage<DocumentAccessLogRow>> {
  const res = await axios.get<CursorPage<DocumentAccessLogRow>>(
    `${ADMIN_BASE}/access-log/`,
    { params }
  );
  return res.data;
}


// ---------------------------------------------------------------------------
// Documents audit — per-document compliance surface (retention horizon,
// legal hold, 30-day access count). Distinct from
// `listIdentityVerifications` (fraud-review queue) and the bare
// VerificationDocument viewset (per-doc detail) — the rollups + bulk
// employee resolution live in their own endpoint so each surface only
// pays for what it renders.
// ---------------------------------------------------------------------------

export type DocumentRetentionStatus =
  | 'active'
  | 'expiring_30d'
  | 'expired'
  | 'purged'
  | 'held';

export interface DocumentAuditItem {
  id: string;
  employee_id: number;
  employee_name: string | null;
  employer_name: string | null;
  doc_type: string;
  document_kind: 'sa_id' | 'passport' | null;
  captured_at: string;
  retention_expires_at: string;
  purged_at: string | null;
  has_legal_hold: boolean;
  legal_hold_placed_by: string | null;
  legal_hold_placed_at: string | null;
  legal_hold_reason: string | null;
  access_count_30d: number;
  // `null` for now — backend hasn't backfilled GCS object size into the
  // metadata blob yet. Forward-compatible so the UI column can ship
  // before the data does.
  size_bytes: number | null;
}

export interface DocumentsAuditStats {
  total: number;
  on_hold: number;
  due_within_30d: number;
  purged: number;
}

interface ListDocumentsAuditParams {
  employee_id?: number;
  doc_types?: string;
  document_kind?: 'sa_id' | 'passport';
  legal_hold?: boolean;
  retention_status?: DocumentRetentionStatus;
  since?: string;
  until?: string;
  cursor?: string;
}

async function listDocumentsAudit(
  params: ListDocumentsAuditParams = {}
): Promise<CursorPage<DocumentAuditItem> & { stats: DocumentsAuditStats }> {
  // Coerce booleans to the truthy strings the backend's `_parse_bool`
  // expects — axios serializes raw booleans as 'true'/'false' which
  // happens to match, but being explicit avoids future surprise.
  const { legal_hold, ...rest } = params;
  const serialized: Record<string, string | number | undefined> = { ...rest };
  if (legal_hold !== undefined) {
    serialized.legal_hold = legal_hold ? 'true' : 'false';
  }
  const res = await axios.get<
    CursorPage<DocumentAuditItem> & { stats: DocumentsAuditStats }
  >(`${ADMIN_BASE}/documents-audit/`, { params: serialized });
  return res.data;
}


// ---------------------------------------------------------------------------
// Person aggregate — per-employee fraud-risk rollup. Drives the
// Employees sub-tab on the Identity Verifications page and the fraud
// banner on EmployeeDetail > Verification.
// ---------------------------------------------------------------------------

export interface PersonAggregate {
  employee_id: number;
  employee_name: string | null;
  employer_name: string | null;
  bsuid: string | null;
  msisdn: string | null;
  last_seen: string;
  risk_score: FraudSeverity;
  session_count: number;
  mismatch_count: number;
  open_signals: string[];
}

interface ListPeopleParams {
  risk?: FraudSeverity;
  search?: string;
  cursor?: string;
}

async function listPeople(
  params: ListPeopleParams = {}
): Promise<CursorPage<PersonAggregate>> {
  const res = await axios.get<CursorPage<PersonAggregate>>(`${ADMIN_BASE}/people/`, {
    params,
  });
  return res.data;
}

async function getPerson(employeeId: number): Promise<PersonAggregate | null> {
  try {
    const res = await axios.get<PersonAggregate>(
      `${ADMIN_BASE}/people/${employeeId}/`
    );
    return res.data;
  } catch (err) {
    // 404 = no verification activity for this employee. The banner uses
    // this to suppress itself rather than render with empty fields. Any
    // other error bubbles up so the caller can decide what to do.
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw err;
  }
}


// ---------------------------------------------------------------------------
// Flow session detail — full bundle (selfie + ID attempts + consent +
// credentials_written + derived outcome) for the SessionDetailPage on
// admin-ui. `decision` is always null until SessionReview lands.
// ---------------------------------------------------------------------------

export interface FlowSessionCredentialEntry {
  id: string;
  requirement_code: string | null;
  event: 'granted' | 'revoked' | 'expired';
  source_type: string;
  trigger_event: string;
  valid_from: string | null;
  valid_until: string | null;
  date_created: string | null;
}

export type FlowSessionOutcome = 'matched' | 'mismatch' | 'pending' | 'inconclusive';

export interface FlowSessionDetail {
  id: string;
  employee_id: number;
  employee_name: string | null;
  employer_name: string | null;
  consent_version: string | null;
  consent_accepted_at: string | null;
  selfie: IdentityVerificationListItem | null;
  id_attempts: IdentityVerificationListItem[];
  other_documents: IdentityVerificationListItem[];
  started_at: string | null;
  duration_seconds: number | null;
  outcome: FlowSessionOutcome;
  bsuid_at_capture: string | null;
  msisdn_at_capture: string | null;
  credentials_written: FlowSessionCredentialEntry[];
  decision: null;
}

// Per-session rollup queue — one row per distinct `flow_session_id`,
// aggregating selfie + ID attempts + retries into a single row with
// derived outcome / worst severity. Drives the Sessions sub-tab on the
// Identity Verifications page. Distinct from `listIdentityVerifications`
// (per-document fraud queue) and `listDocumentsAudit` (per-document
// compliance view).
// Shared with FlowSessionDetail.outcome — same derivation feeds both
// the list rollup and the detail view (see flow_session.list_flow_sessions
// in django).
export interface FlowSessionListItem {
  flow_session_id: string;
  employee_id: number;
  employee_name: string | null;
  employer_name: string | null;
  started_at: string | null;
  last_activity_at: string | null;
  duration_seconds: number | null;
  outcome: FlowSessionOutcome;
  doc_count: number;
  selfie_present: boolean;
  id_attempts_count: number;
  // Highest-badness fraud_severity across the session's docs. Null when
  // no doc carries one (selfie-only sessions, legacy uploads).
  worst_severity: FraudSeverity | null;
  bsuid_at_capture: string | null;
  msisdn_at_capture: string | null;
  consent_version: string | null;
}

interface ListFlowSessionsParams {
  employee_id?: number;
  since?: string;
  until?: string;
  cursor?: string;
}

async function listFlowSessions(
  params: ListFlowSessionsParams = {}
): Promise<CursorPage<FlowSessionListItem>> {
  const res = await axios.get<CursorPage<FlowSessionListItem>>(
    `${ADMIN_BASE}/flow-sessions/`,
    { params }
  );
  return res.data;
}


async function getFlowSession(flowSessionId: string): Promise<FlowSessionDetail | null> {
  try {
    const res = await axios.get<FlowSessionDetail>(
      `${ADMIN_BASE}/flow-sessions/${flowSessionId}/`
    );
    return res.data;
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw err;
  }
}


// ---------------------------------------------------------------------------
// Reviewer verdict submission — POST /admin/documents/<id>/review/ writes
// `reviewer_note` and / or `review_outcome` on the document and lands a
// DocumentReviewLog row in the same transaction. `reason` is required;
// callers should treat the empty string as "clear the note" but null as
// "leave alone" via key omission.
// ---------------------------------------------------------------------------

// Payload note on null/undefined:
//   * Omitting a key entirely → field is unchanged.
//   * Sending the key with null (or "" for reviewer_note) → field is cleared.
// Use `null` not `undefined` when you mean "clear" — axios drops undefined
// values before serialization, which would silently turn a "clear" into a
// "leave alone".
export interface DocumentReviewSubmitPayload {
  reviewer_note?: string | null;
  review_outcome?: ReviewOutcome | null;
  reason: string;
}

export interface DocumentReviewLogEntry {
  id: string;
  document: string;
  diff: {
    before?: { reviewer_note?: string | null; review_outcome?: ReviewOutcome | null };
    after?: { reviewer_note?: string | null; review_outcome?: ReviewOutcome | null };
  };
  actor: string;
  reason: string;
  source_ip: string | null;
  user_agent: string;
  date_created: string;
}

// VerificationDocument as returned by the document detail / review
// endpoints. Carries the full metadata blob; the per-row queue view
// uses `IdentityVerificationListItem` (lifted fields) instead.
export interface VerificationDocument {
  id: string;
  employee_id: number;
  doc_type: string;
  mime_type: string;
  checksum_sha256: string;
  verification_status: string;
  captured_at: string;
  bsuid_at_capture: string | null;
  msisdn_at_capture: string | null;
  retention_expires_at: string;
  purged_at: string | null;
  reviewer_note: string | null;
  reviewer_note_set_by: string | null;
  reviewer_note_set_at: string | null;
  review_outcome: ReviewOutcome | null;
  review_outcome_set_by: string | null;
  review_outcome_set_at: string | null;
  is_decryptable: boolean;
  metadata: Record<string, unknown>;
  date_created: string;
}

// Signed-URL response from POST /admin/documents/<id>/signed-url/. The URL
// is short-lived (default 5 minutes); the UI should treat it as ephemeral
// and not persist beyond the current panel render.
//
// NOTE: the URL points at the raw GCS object, which holds envelope-encrypted
// ciphertext — it is NOT directly viewable. Only useful to a caller that
// already holds the unwrapped DEK. To display a document, use
// `getDocumentImageDataUrl` below, which decrypts server-side.
export interface DocumentSignedUrl {
  url: string;
  signed_url_id: string;
  expires_at: string;
}

async function getDocumentSignedUrl(
  documentId: string,
  options: { reason: string; ticketRef?: string; expiresInSeconds?: number }
): Promise<DocumentSignedUrl> {
  const headers: Record<string, string> = {
    'X-Access-Reason': options.reason,
  };
  if (options.ticketRef) headers['X-Ticket-Ref'] = options.ticketRef;
  const body: Record<string, unknown> = {};
  if (options.expiresInSeconds !== undefined) {
    body.expires_in_seconds = options.expiresInSeconds;
  }
  const res = await axios.post<DocumentSignedUrl>(
    `${ADMIN_BASE}/documents/${documentId}/signed-url/`,
    body,
    { headers }
  );
  return res.data;
}

// With `responseType: 'blob'` axios hands back error bodies as Blobs too, so
// `err.response.data.detail` — which the reveal call sites read to surface the
// server's message ("document purged", bad access reason) — would come back
// undefined. Re-parse the body into JSON so that contract keeps holding.
async function reviveBlobErrorBody(err: unknown): Promise<unknown> {
  const response = (err as { response?: { data?: unknown } })?.response;
  if (response?.data instanceof Blob) {
    try {
      response.data = JSON.parse(await response.data.text());
    } catch {
      // Non-JSON body (HTML error page, empty response) — leave it alone and
      // let the call site fall back to its generic message.
    }
  }
  return err;
}

// Fetch decrypted document bytes as a `data:` URL, ready for an <img src>.
//
// Deliberately the `download` endpoint, not `signed-url`: objects in GCS hold
// envelope-encrypted ciphertext, so a signed URL serves bytes the browser
// cannot decode. It renders nothing, silently — GCS returns 200 with
// `content-type: application/octet-stream` and no error surfaces anywhere.
// Only django holds the unwrapped DEK, so decryption has to happen server-side.
//
// A `data:` URL rather than `URL.createObjectURL` because the admin CSP allows
// `data:` in `img-src` but not `blob:` (blob is only permitted in `worker-src`).
//
// Writes a `download` DocumentAccessLog row server-side, same as signed-url.
async function getDocumentImageDataUrl(
  documentId: string,
  options: { reason: string; ticketRef?: string }
): Promise<string> {
  const headers: Record<string, string> = {
    'X-Access-Reason': options.reason,
  };
  if (options.ticketRef) headers['X-Ticket-Ref'] = options.ticketRef;

  let res;
  try {
    res = await axios.post<Blob>(
      `${ADMIN_BASE}/documents/${documentId}/download/`,
      {},
      { headers, responseType: 'blob' }
    );
  } catch (err) {
    throw await reviveBlobErrorBody(err);
  }

  const contentType = (res.headers['content-type'] as string) || 'application/octet-stream';
  const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: contentType });
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read document bytes.'));
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });
}

async function submitDocumentReview(
  documentId: string,
  payload: DocumentReviewSubmitPayload
): Promise<VerificationDocument> {
  const res = await axios.post<VerificationDocument>(
    `${ADMIN_BASE}/documents/${documentId}/review/`,
    payload
  );
  return res.data;
}

async function listDocumentReviewLog(
  documentId: string
): Promise<DocumentReviewLogEntry[]> {
  const res = await axios.get<DocumentReviewLogEntry[]>(
    `${ADMIN_BASE}/documents/${documentId}/review-log/`
  );
  return res.data;
}

// Re-run the ID extraction cascade against a stored document. Two-step:
//   1. POST /admin/documents/<id>/download/ — audited; returns decrypted
//      bytes inline (X-Access-Reason header required, same as signed-url).
//   2. POST /verification/bench/id — non-persisting analyzer; runs the
//      zxing-cpp + MRZ + OCR cascade on the bytes and returns the result.
//
// The analyzer endpoint is shared with the bench tab; the response shape
// is `IdExtractionResponse` (alias of the bench tab's IdResponse, matched
// structurally so IdExtractionResultCard can render it). The download
// step writes a DocumentAccessLog row of kind=`download`.
export interface IdExtractionResponse {
  ok: boolean;
  latency_ms: number;
  results: Array<{
    document_kind: 'sa_id' | 'passport';
    ok: boolean;
    id_number: string | null;
    method: string | null;
    reason: string | null;
    attempts: Array<{
      method: string;
      ok: boolean;
      reason: string | null;
      detail?: string | null;
    }>;
    extra_fields?: Record<string, string | null | undefined>;
    fraud_signals?: Record<string, unknown>;
    fraud_severity?: string;
  }>;
  image?: Record<string, unknown>;
}

async function runDocumentIdExtraction(
  documentId: string,
  options: { reason: string; ticketRef?: string }
): Promise<IdExtractionResponse> {
  const headers: Record<string, string> = {
    'X-Access-Reason': options.reason,
  };
  if (options.ticketRef) headers['X-Ticket-Ref'] = options.ticketRef;

  // Step 1 — fetch decrypted bytes. We use the download endpoint rather
  // than re-fetching the GCS signed URL because the GCS object holds
  // envelope-encrypted ciphertext; only the django side has the unwrapped
  // DEK to decrypt.
  const bytesRes = await axios.post<Blob>(
    `${ADMIN_BASE}/documents/${documentId}/download/`,
    {},
    { headers, responseType: 'blob' }
  );
  const contentType = (bytesRes.headers['content-type'] as string) || 'application/octet-stream';
  const blob = bytesRes.data instanceof Blob ? bytesRes.data : new Blob([bytesRes.data], { type: contentType });

  // Step 2 — POST to the analyzer. `auto` runs both cascades (SA-ID +
  // passport) and returns whichever decoded; saves the admin from having
  // to guess the document kind.
  const form = new FormData();
  form.append('image', blob, 'document');
  form.append('document_kind', 'auto');
  form.append('allow_gemini', 'false');
  const extractRes = await axios.post<IdExtractionResponse>(
    '/api/verification/bench/id',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return extractRes.data;
}

// ---------------------------------------------------------------------------
// Admin-side policy check (for ad-hoc admin testing of "would this employee be
// allowed to do X"). Uses the admin endpoint — same evaluator as the chatbot
// `/policies/<code>` route but gated on AdminPermission and skips the
// ActionAttempt audit write so admin testing doesn't pollute the runtime log.
// ---------------------------------------------------------------------------

async function testPolicyCheck(
  actionCode: string,
  params: {
    employee_id: number;
    channel_branding?: string;
    channel_identity?: string;
    is_proxy?: boolean;
    amount_cents?: number;
    location?: string;
  }
): Promise<PolicyDecision> {
  const res = await axios.get<PolicyDecision>(`${ADMIN_BASE}/check/${actionCode}`, { params });
  return res.data;
}


// Bulk roll-up of every action's decision for one employee. Same evaluator
// as the chatbot endpoint of the same name, but on the admin URL — gated
// on AdminPermission and skips the ActionAttempt audit write.
async function getAllowedActions(
  employeeId: number,
  params: {
    channel_branding?: string;
    channel_identity?: string;
    is_proxy?: boolean;
    amount_cents?: number;
    location?: string;
  } = {}
): Promise<AllowedActions> {
  const res = await axios.get<AllowedActions>(
    `${ADMIN_BASE}/employees/${employeeId}/allowed-actions`,
    { params }
  );
  return res.data;
}

export const verificationClient = {
  // categories
  listActionCategories,
  getActionCategory,
  createActionCategory,
  updateActionCategory,
  deleteActionCategory,
  listRequirementCategories,
  getRequirementCategory,
  createRequirementCategory,
  updateRequirementCategory,
  deleteRequirementCategory,
  // registries
  listActions,
  getAction,
  createAction,
  updateAction,
  deleteAction,
  listRequirements,
  getRequirement,
  createRequirement,
  updateRequirement,
  deleteRequirement,
  // policies
  listPolicies,
  getPolicy,
  createPolicy,
  updatePolicy,
  deletePolicy,
  listEmployerPolicies,
  getEmployerPolicy,
  upsertEmployerPolicy,
  deleteEmployerPolicy,
  // ledger
  listEmployeeCredentials,
  getCurrentEmployeeCredentials,
  // audit
  listActionAttempts,
  listPolicyChangeLog,
  listIdentityVerifications,
  listAccessLog,
  listDocumentsAudit,
  // people + sessions (fraud-review surfaces)
  listPeople,
  getPerson,
  listFlowSessions,
  getFlowSession,
  // reviewer verdicts
  submitDocumentReview,
  listDocumentReviewLog,
  // image reveal (writes a DocumentAccessLog row server-side).
  // `getDocumentImageDataUrl` is the one to use for display — it decrypts
  // server-side. `getDocumentSignedUrl` returns a URL to raw ciphertext.
  getDocumentImageDataUrl,
  getDocumentSignedUrl,
  // re-run ID extraction on a stored doc (download → analyzer cascade;
  // writes a `download` DocumentAccessLog row)
  runDocumentIdExtraction,
  // ad-hoc
  testPolicyCheck,
  getAllowedActions,
};
